/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useRef } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, func, string,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import Input from '@material-ui/core/Input';
import TextField from '@material-ui/core/TextField';

const styles = (theme) => ({
    main: {
        width: '633px',
        minHeight: '250px',
        margin: '15px 0px 18px 0px',
        [theme.breakpoints.up('md')]: {
            minHeight: 270,
        },
    },
    cardTitle: {
        color: theme.palette.submit,
        fontSize: '18px',
        textAlign: 'left',
        fontWeight: '600',
        margin: '14px',
        lineHeight: 'normal',
    },
    cardText: {
        fontSize: '13px',
        textAlign: 'left',
        color: theme.palette.colorNormalText,
        lineHeight: 'normal',
        margin: '12px',
    },
    textFieldWrapper: {
        flexDirection: 'row',
        justifyContent: 'center',
        display: 'flex',
    },
    textField: {
        minHeight: '45px',
        margin: '0px 0px 0px 15px',
        '& .MuiFormHelperText-root': {
            fontSize: '9px',
        },
        '& .MuiInputBase-input': {
            paddingTop: 9,
            textAlign: 'center',
        },
    },
    handleMessage: {
        '&.error::before': {
            top: 20,
        },
        '&.error::after': {
            left: 20,
        },
        '&.success::after': {
            left: 15,
            padding: 5,
        },
    },
    verificationContainer: {
        display: 'flex',
        flexDirection: 'row',
    },
    zipcode: {
        width: '50%',
        '& .MuiInputBase-input': {
            height: '29px',
            border: `1px solid ${theme.palette.cms?.wineVerifyDisable}`,
            backgroundColor: theme.palette.primary?.contrastText,
            '&:disabled': {
                backgroundColor: theme.palette.cms?.wineVerifyDisable,
            },
        },
    },
    birth: {
        width: '50%',
        justifyContent: 'center',
    },
    submit: {
        color: theme.palette.white,
        backgroundColor: `${theme.palette.submit || theme.palette.colorNeutral80}`,
        fontFamily: theme.typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '90px',
        height: '35px',
        fontSize: '13px',
        borderRadius: '28px',
        lineHeight: '1.46em',
        margin: '21px 0px 0px 0px',
        '&:hover, &:focus, &:active': {
            backgroundColor: theme?.palette?.colorPrimary,
        },
        '&:disabled': {
            backgroundColor: `${theme.palette.cms?.wineVerifyDisable || theme.palette.colorNeutral20}`,
            color: theme.palette.colorNeutral80,
        },
    },
    form: {
        width: '85px',
        border: `1px solid ${theme.palette.colorNeutral80}`,
        float: 'left',
        margin: '15px 15px 0px 35px',
    },
    zipField: {
        width: '47px',
        height: '45px',
        margin: '0px 0px 0px 15px',
        '& .MuiFormHelperText-root': {
            fontSize: '7px',
        },
    },
    zipCodeInput: {
        width: '100%',
        display: 'block',  // IE 11
        '& > div': {
            width: '100%', // IE 11
        },
    },
    ageInput: {
        width: '100%',
        display: 'flex',  // IE 11
        padding: '8px 0px 8px 0px',
        '& > div': {
            width: '100%', // IE 11
        },
    },
    enterDateBirth: {
        margin: '14px',
        fontSize: '15px',
        height: '22px',
        fontWeight: '700',
    },
    enterZipCodeTitle: {
        margin: '14px 80px 14px 0px',
        fontSize: '15px',
        height: '22px',
        fontWeight: '700',
        textAlign: 'center',
        '&:disabled': {
            backgroundColor: theme.palette.cms?.wineVerifyDisable,
        },
    },
    enterBirthInput: {
        width: '55px',
        height: '38px',
        border: `1px solid ${theme.palette.grey8}`,
        fontSize: '16px',
        margin: 0,
    },
    enterBirthYearInput: {
        width: '65px',
        height: '40px',
        border: `1px solid ${theme.palette.grey8}`,
        fontSize: '16px',
        margin: 0,
    },
    grid: {
        width: '250px',
        height: '90px',
        '&. .MuiFormControl-marginDense': {
            margin: '0px',
        },
    },
    textCenter: {
        textAlign: 'center',
    },
    zipResponseMessage: {
        '&::after': {
            top: 25,
        },
        '&.zip-code-error::after': {
            top: 0,
        },
    },
});

const ProductVerification = ({
    classes,
    zipField,
    dayErrorMessage,
    monthErrorMessage,
    yearErrorMessage,
    zipFieldRef,
    getBirthDay,
    getBirthMonth,
    getBirthYear,
    handleAgeVerification,
    validateFields,
    handleZipChange,
    displayZipValidCSSCheck,
    validateZip,
    birthDay,
    birthMonth,
    birthYear,
}) => {
    const yearFieldRef = useRef(null);
    const monthFieldRef = useRef(null);
    const dayFieldRef = useRef(null);
    return (
        <>
            <Card className={classes.main}>
                <h3 className={classes.cardTitle}>
                    Confirm age and destination for wine purchase.
                </h3>
                <p className={classes.cardText}>
                    I am at least 21 years of age and I understand that any person accepting delivery of
                    alcoholic beverages must be at least 21 years of age and be home to sign for the delivery.
                </p>
                <div className={classes.verificationContainer} data-testid="age-verification-model">
                    <div className={classes.birth}>
                        <p className={classes.enterDateBirth}>
                            Enter Your Date Of Birth
                        </p>
                        <Grid container direction="row" className={classes.grid}>
                            <div className={classes.textField}>
                                <div>Month:</div>
                                <TextField
                                    margin="dense"
                                    inputRef={monthFieldRef}
                                    id="month"
                                    type="tel"
                                    placeholder="MM"
                                    onChange={(e) => {
                                        getBirthMonth(e);
                                        if (e.target.value.length === 2 && e.target.value <= 12) {
                                            dayFieldRef.current.focus();
                                        }
                                    }}
                                    className={classes.enterBirthInput}
                                    onBlur={validateFields}
                                    onKeyUp={validateFields}
                                    value={birthMonth}
                                    inputProps={{
                                        shrink: true,
                                        pattern: '[0-9]/g',
                                        maxLength: 2,
                                        min: 1,
                                        max: 12,
                                    }}
                                    error={monthErrorMessage !== ''}
                                    helperText={monthErrorMessage}
                                />
                            </div>
                            <div className={classes.textField}>
                                <div>Day:</div>
                                <TextField
                                    margin="dense"
                                    inputRef={dayFieldRef}
                                    id="day"
                                    type="tel"
                                    placeholder="DD"
                                    onChange={(e) => {
                                        getBirthDay(e);
                                        if (e.target.value.length === 2 && e.target.value <= 31) {
                                            yearFieldRef.current.focus();
                                        }
                                    }}
                                    className={classes.enterBirthInput}
                                    value={birthDay}
                                    onBlur={validateFields}
                                    onKeyUp={validateFields}
                                    inputProps={{
                                        maxLength: 2,
                                        min: 1,
                                        max: 31,
                                    }}
                                    error={dayErrorMessage !== ''}
                                    helperText={dayErrorMessage}
                                />
                            </div>
                            <div className={classes.textField}>
                                <div>Year:</div>
                                <TextField
                                    margin="dense"
                                    inputRef={yearFieldRef}
                                    id="year"
                                    type="tel"
                                    placeholder="YYYY"
                                    className={classes.enterBirthYearInput}
                                    value={birthYear}
                                    onChange={getBirthYear}
                                    onKeyUp={validateFields}
                                    onBlur={validateFields}
                                    inputProps={{
                                        maxLength: 4,
                                    }}
                                    error={yearErrorMessage !== ''}
                                    helperText={yearErrorMessage}
                                />
                            </div>
                            <div disabled={!handleAgeVerification().flag} className={`${handleAgeVerification().class} ${classes.handleMessage}`} />
                        </Grid>
                    </div>
                    <div className={classes.zipcode}>
                        <p className={classes.enterZipCodeTitle}>
                            Enter destination ZIP code
                        </p>
                        <div className={classes.form}>
                            <Input
                                disabled={!handleAgeVerification().flag}
                                margin="normal"
                                type="tel"
                                id="zipcode-text-field"
                                data-test="pdp-zipcode-text-field"
                                name="zipcodeTextField"
                                onChange={handleZipChange}
                                // defaultValue={userSubmittedZip}
                                value={zipField}
                                inputRef={zipFieldRef}
                                inputProps={{
                                    maxLength: 5,
                                    className: classes.textCenter,
                                }}
                            />
                        </div>
                        <Button
                            disabled={!handleAgeVerification().flag}
                            className={classes.submit}
                            id="confirm-age-zip"
                            onClick={validateZip}
                        >
                            Confirm
                        </Button>
                        <div className={`${displayZipValidCSSCheck().classes} ${classes.zipResponseMessage}`} />
                    </div>
                </div>
            </Card>
        </>
    );
};
ProductVerification.propTypes = {
    classes: object.isRequired,
    zipField: string.isRequired,
    dayErrorMessage: string.isRequired,
    monthErrorMessage: string.isRequired,
    yearErrorMessage: string.isRequired,
    zipFieldRef: func.isRequired,
    birthDay: string.isRequired,
    birthMonth: string.isRequired,
    birthYear: string.isRequired,
    getBirthDay: func.isRequired,
    getBirthMonth: func.isRequired,
    getBirthYear: func.isRequired,
    handleAgeVerification: func.isRequired,
    validateFields: func.isRequired,
    handleZipChange: func.isRequired,
    displayZipValidCSSCheck: func.isRequired,
    validateZip: func.isRequired,
};
export default withStyles(styles)(ProductVerification);
